import Backbone from "backbone";

import readlangClient from "readlangClient";

/**
 * This model just reads data from the server, it doesn't write, and it will re-fetch
 * when the user returns to the tab or every hour.
 */
abstract class AutoRefetchingModel<T> extends Backbone.Model<T> {
  timeoutID: number | undefined;
  lastRefetchTime = new Date().getTime();
  minimumRefetchInterval = 5 * 60 * 1000;
  normalRefetchInterval = 60 * 60 * 1000;

  constructor(attributes?: T, options?: Backbone.ModelSetOptions) {
    super(attributes, options);

    this.timeoutID = setInterval(
      () => this.refetchIfAppropriate(),
      this.normalRefetchInterval
    );

    // Re-fetch when document re-gains visibility (e.g. when switching tabs)
    // Note: This event listener is never removed, which is OK as long as we only ever expect to
    // instantiate one of each kind of these models.
    document.addEventListener("visibilitychange", () => {
      this.refetchIfAppropriate();
    });
  }

  async refetchIfAppropriate() {
    if (this.timeoutID) {
      clearTimeout(this.timeoutID);
    }

    if (document.visibilityState !== "visible") {
      return;
    }

    if (await readlangClient.isLocalMode()) {
      // Don't refetch if we're in local mode
      return;
    }

    const now = new Date().getTime();
    const timeSinceLastRefetch = now - this.lastRefetchTime;
    if (timeSinceLastRefetch > this.minimumRefetchInterval) {
      this.lastRefetchTime = now;
      this.fetch();

      // Re-fetch according to the normalRefetchInterval
      this.timeoutID = setTimeout(
        () => this.refetchIfAppropriate(),
        this.normalRefetchInterval
      );
    }
  }
}

export default AutoRefetchingModel;
